import { useState, useCallback, useEffect } from "react";
import * as locationConfigApi from "../api";
import { findNode } from "../utils";
import { useConfigResolution } from "./useConfigResolution";
import { useLocationConfigCrud } from "./useLocationConfigCrud";
import { useTemplateState } from "./useTemplateState";
import { useWizardState } from "./useWizardState";

export function useLocationConfigManagerHook() {
  const [services, setServices] = useState([]);
  const [resources, setResources] = useState([]);
  const [activeTab, setActiveTab] = useState("browse");
  const [selectedLocation, setSelectedLocation] = useState("APDS");
  const [searchText, setSearchText] = useState("");
  const [expandedNodes, setExpandedNodes] = useState(new Set(["ga", "APDS", "tn"]));
  const [toast, setToast] = useState(null);

  const [locationsTree, setLocationsTree] = useState([]);
  const [apiConfig, setApiConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [apiTemplates, setApiTemplates] = useState([]);
  const [servicesRef, setServicesRef] = useState([]);
  const [resourcesRef, setResourcesRef] = useState([]);

  useEffect(() => {
    locationConfigApi
      .getLocations()
      .then(setLocationsTree)
      .catch(() => setLocationsTree([]));
  }, []);

  useEffect(() => {
    if (!locationsTree?.length) return;
    if (findNode(locationsTree, selectedLocation)) return;
    const firstId = locationsTree[0]?.id;
    if (firstId) setSelectedLocation(firstId);
  }, [locationsTree, selectedLocation]);

  useEffect(() => {
    setServicesRef([]);
    setResourcesRef([]);
    locationConfigApi.getServices().then(setServicesRef).catch(() => {});
    locationConfigApi.getResources().then(setResourcesRef).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedLocation) return;
    setConfigLoading(true);
    setConfigError(null);
    locationConfigApi
      .getLocationConfig(selectedLocation)
      .then((data) => {
        setApiConfig(data);
        setConfigLoading(false);
      })
      .catch((err) => {
        setConfigError(err?.message || "Failed to load config");
        setApiConfig(null);
        setConfigLoading(false);
      });
  }, [selectedLocation]);

  useEffect(() => {
    locationConfigApi
      .getTemplates()
      .then(setApiTemplates)
      .catch(() => setApiTemplates([]));
  }, []);

  const refetchConfig = useCallback(() => {
    if (!selectedLocation) return;
    locationConfigApi.getLocationConfig(selectedLocation).then(setApiConfig).catch(() => {});
  }, [selectedLocation]);

  const refetchTemplates = useCallback(() => {
    return locationConfigApi.getTemplates().then(setApiTemplates);
  }, []);

  const notify = useCallback((message, type = "success") => setToast({ message, type }), []);

  const { getConfig, config, locationNode, locationChain } = useConfigResolution(
    services,
    resources,
    selectedLocation,
    {
      locationsTree: locationsTree ?? [],
      configFromApi: apiConfig ?? undefined,
    }
  );

  const crud = useLocationConfigCrud(
    apiConfig?.services ?? [],
    setServices,
    apiConfig?.resources ?? [],
    setResources,
    notify,
    {
      api: locationConfigApi,
      locationId: selectedLocation,
      refetchConfig,
      servicesRef,
      resourcesRef,
    }
  );

  const template = useTemplateState(
    apiTemplates ?? [],
    getConfig,
    setActiveTab,
    {
      api: locationConfigApi,
      locations: locationsTree ?? [],
      refetchTemplates,
      resourcesRef,
      servicesRef,
    }
  );

  const wizard = useWizardState({
    services,
    setServices,
    resources,
    setResources,
    templates: template.templates,
    getConfig,
    notify,
    setTab: setActiveTab,
    locationsTree: locationsTree ?? [],
    apiOpts: {
      api: locationConfigApi,
      servicesRef,
      resourcesRef,
      refetchConfig,
    },
  });

  const toggleExpanded = useCallback((id) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleTabClick = useCallback(
    (tabId) => {
      setActiveTab(tabId);
      if (tabId === "wizard") wizard.wizardReset();
      if (tabId === "templates") template.setEditingTemplate(null);
    },
    [template, wizard]
  );

  const saveTemplateWithNotify = useCallback(() => {
    template.saveTemplate(notify);
  }, [template, notify]);

  const deleteTemplateWithNotify = useCallback(
    (id) => {
      template.deleteTemplate(id, notify);
    },
    [template, notify]
  );

  const toggleTemplateExpandedService = useCallback((index) => {
    template.setTemplateExpandedServices((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  }, [template]);

  const toggleTemplateExpandedResource = useCallback((index) => {
    template.setTemplateExpandedResources((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  }, [template]);

  const toggleWizardExpanded = useCallback((id) => {
    wizard.setWizardExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, [wizard]);

  const clearToast = useCallback(() => setToast(null), []);

  return {
    activeTab,
    setActiveTab,
    searchText,
    setSearchText,
    selectedLocation,
    setSelectedLocation,
    expandedNodes,
    toggleExpanded,
    handleTabClick,
    toast,
    clearToast,
    config,
    locationNode,
    locationChain,
    crud,
    template,
    wizard,
    saveTemplateWithNotify,
    deleteTemplateWithNotify,
    toggleTemplateExpandedService,
    toggleTemplateExpandedResource,
    toggleWizardExpanded,
    locationsTree: locationsTree ?? [],
    configLoading,
    configError,
    servicesRef,
    resourcesRef,
  };
}
